import { AppIntegrationsRepository, AppInstancesRepository, DevicesRepository } from 'common/repositories/index';
import Util from 'common/services/util/-RFC.util';
/**
 * Instance Processor - instances grouped by userId and device
 * The instances for the user based integrations(scope = user)
 * must be taken from the AppInstancesRepository
 */

// these are the app instances grouped by userId
let processedAppInstances = [];

const observableProcessedAppInstances = new Rx.BehaviorSubject(processedAppInstances);

const AppInstancesProcessor = {

    /**
     * Replace all instances with received data
     * @param {Array} data
     */
    onNext(data) {

        processedAppInstances = [];
        data.forEach((appInstance) => {

            if (appInstance.relationships.device) {

                this.addAppInstance(appInstance);

            }

        });

        observableProcessedAppInstances.onNext(processedAppInstances);

    },

    /**
     * Add appInstance based on its userId and state
     * @param appInstance
     */
    addAppInstance(appInstance) {

        const processedAppInstance = angular.copy(appInstance);
        const userId = appInstance.relationships.user.data.id;
        const deviceId = appInstance.relationships.device.data.id;
        const appIntegration = AppIntegrationsRepository
            .getById(appInstance.relationships.app.data.id);

        const appName = Util.deepFind(appIntegration, 'attributes.resource.oeName');
        const appShortDescription = Util.deepFind(appIntegration, 'attributes.resource.shortDescription');
        const appIcon = Util.deepFind(appIntegration, 'attributes.resource.wsLargeImageUrl');
        const prototype = Util.deepFind(appIntegration, 'attributes.prototype');
        const order = Util.deepFind(appIntegration, 'attributes.custom.manifest.order');
        const appType = Util.deepFind(appIntegration, 'attributes.custom.manifest.appType');
        const browsers = Util.deepFind(appIntegration, 'attributes.custom.manifest.browsers');
        const os = Util.deepFind(appIntegration, 'attributes.custom.manifest.os');
        const visible = Util.deepFind(appIntegration, 'attributes.custom.manifest.visible');

        processedAppInstances[userId] = processedAppInstances[userId] || [];
        processedAppInstances[userId][deviceId] =
            processedAppInstances[userId][deviceId] || [];

        processedAppInstance.customAppInfo = {
            name: appName,
            shortDescription: appShortDescription,
            icon: appIcon,
            prototype,
            order,
            appType,
            browsers,
            os,
            visible,
        };
        processedAppInstances[userId][deviceId].push(processedAppInstance);

    },

    getObservable() {

        return observableProcessedAppInstances;

    },

    /**
     * @ngdoc function
     * @name processors.InstanceProcessor#getAll
     * @methodOf processors.InstanceProcessor
     * @function
     *
     * @description
     * This function can be used to return all processed instances.
     *
     * @returns {Array} all processed instances
     */

    getAll() {

        return processedAppInstances;

    },

    /**
     * Returns the instances of an user for a specific integration.
     * @param userId
     * @param integrationId
     */
    getByAppIntegration(userId, integrationId) {

        const instances = [];

        if (!processedAppInstances[userId]) {

            return instances;

        }

        _.values(processedAppInstances[userId]).forEach((deviceAppInstances) => {

            deviceAppInstances.forEach((instance) => {

                if (String(instance.relationships.app.data.id) === String(integrationId)) {

                    instances.push(instance);

                }

            });

        });

        return instances;

    },

    /**
     * Returns the instances of an user for a specific prototype.
     * @param userId
     * @param prototype
     */
    getInstancesByPrototype(userId, prototype) {

        const instances = [];

        if (!processedAppInstances[userId]) {

            return instances;

        }

        _.values(processedAppInstances[userId]).forEach((deviceAppInstances) => {

            deviceAppInstances.forEach((instance) => {

                if (Util.deepFind(instance, 'customAppInfo.prototype') === prototype &&
                Util.deepFind(instance, 'attributes.state') !== 'pending') {

                    const device = DevicesRepository
                        .getById(Util.deepFind(instance, 'relationships.device.data.id'));
                    if (DevicesRepository.isVisible(device)) {

                        instances.push(instance);

                    }

                }

            });

        });

        return instances;

    },

    /**
     * Returns the instances of an user for a specific id if the param licenseId exists in instance.
     * @param userId
     * @param licenseId
     */
    getInstancesByLicenseId(userId, licenseId) {

        const instances = [];

        _.values(processedAppInstances[userId]).forEach((deviceAppInstances) => {

            deviceAppInstances.forEach((instance) => {

                if (Util.deepFind(instance, 'relationships.license.data.id') === licenseId) {

                    instances.push(instance);

                }

            });

        });

        return instances;

    },

    /**
     * Returns the instances of an user for a specific prototype and a specific device.
     * @param userId
     * @param prototype
     * @param device
     */
    getInstancesByPrototypeAndDevice(userId, prototype, device) {

        const instances = [];

        if (!device) {

            return instances;

        }

        if (!processedAppInstances[userId]) {

            return instances;

        }

        _.values(processedAppInstances[userId][device.id]).forEach((instance) => {

            if (instance.customAppInfo.prototype === prototype) {

                instances.push(instance);

            }

        });

        return instances;

    },


    /**
     * Returns the instances of an user for a specific device.
     * @param userId
     * @param deviceId
     */
    getInstancesByDevice(userId, deviceId) {

        const instances = [];

        if (!processedAppInstances[userId]) {

            return instances;

        }

        return processedAppInstances[userId][deviceId];

    },

    /**
     * Returns the instance of an user for a specific integration and a specific device.
     * @param userId
     * @param integrationId
     * @param deviceId
     */
    getByAppIntegrationAndDevice(userId, integrationId, deviceId) {

        if (!processedAppInstances[userId] ||
            !processedAppInstances[userId][deviceId]) {

            return undefined;

        }

        return processedAppInstances[userId][deviceId]
            .find(appInstance =>
                (String(appInstance.relationships.app.data.id) === String(integrationId) &&
                String(appInstance.relationships.device.data.id) === String(deviceId)));

    },

    /**
     * Returns the instance of an user for a specific prototype and a specific device.
     * @param userId
     * @param prototype
     * @param deviceId
     */
    getByAppPrototypeAndDevice(userId, prototype, deviceId) {

        if (!processedAppInstances[userId] ||
            !processedAppInstances[userId][deviceId]) {

            return undefined;

        }

        return processedAppInstances[userId][deviceId]
            .find(appInstance => appInstance.customAppInfo.prototype === prototype &&
                    String(appInstance.relationships.device.data.id) === String(deviceId));

    },

    /**
     * Checks if a provided instance is antitheft instance or not.
     * It looks after a location in its content.
     * @param {Object} instance The instance object.
     */
    hasLocationInfo(instance) {

        return !!Util.deepFind(instance, 'attributes.status.LOCATE.lastLocation');

    },

};

const subjectAppInstancesRepository = AppInstancesRepository.getObservable();
subjectAppInstancesRepository.subscribe(
    data => AppInstancesProcessor.onNext(data),
    () => { },
    () => { },
);

export default AppInstancesProcessor;



// WEBPACK FOOTER //
// ./scripts/common/repositoryProcessors/-RFC.appInstancesProcessor.js